import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { VisuallyHidden } from 'react-aria';

import { CSS, keyframes, styled, theme } from '../stitches.config';
import { BasicButton } from './basic-button';
import { CrossIcon } from './icons';
import { Icon } from './icons/icon';
import { CloseIcon } from './plans-popup/plans-popup.styles';

type Props = {
  isMobileStickyBottom?: boolean;
  closeOnEscape?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  css?: CSS;
  isExtensionAddToNotes?: boolean;
  portalContainer?: HTMLElement;
};
export const Dialog = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<Props>
>((props, ref) => {
  const {
    open,
    setOpen,
    closeOnEscape,
    isMobileStickyBottom = false,
    isExtensionAddToNotes = false,
    portalContainer,
    ...contentProps
  } = props;
  const [SSRRendered, setSSRRendered] = useState(false);

  // Handle body scroll prevention and focus management for shadow DOM
  useEffect(() => {
    if (!isExtensionAddToNotes || !open) {
      return;
    }

    // Store original values
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const scrollY = window.scrollY;

    // Prevent body scroll when modal is open in extension
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    // Prevent scroll restoration on modal open
    const preventScrollIntoView = (e: Event) => {
      if (e.target && 'scrollIntoView' in e.target) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    // Add event listeners to prevent unwanted scrolling
    document.addEventListener('scroll', preventScrollIntoView, {
      capture: true,
      passive: false,
    });
    window.addEventListener('scroll', preventScrollIntoView, {
      capture: true,
      passive: false,
    });

    return () => {
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = '';

      // Remove event listeners
      document.removeEventListener('scroll', preventScrollIntoView, {
        capture: true,
      });
      window.removeEventListener('scroll', preventScrollIntoView, {
        capture: true,
      });

      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isExtensionAddToNotes, open]);

  useEffect(() => {
    setSSRRendered(true);
  }, []);

  return (
    <AlertDialogPrimitive.Root
      open={props.open && SSRRendered}
      onOpenChange={closeOnEscape ?? true ? props.setOpen : undefined}
    >
      <AlertDialogPrimitive.AlertDialogPortal container={portalContainer}>
        <StyledOverlay
          css={{
            zIndex: isExtensionAddToNotes ? 2147483646 : 1001,
          }}
          {...(isExtensionAddToNotes && {
            onClick: (e) => {
              // Prevent overlay clicks from bubbling to parent document
              if (isExtensionAddToNotes) {
                e.stopPropagation();
              }
            },
          })}
        />
        <StyledContent
          isMobileStickyBottom={isMobileStickyBottom}
          ref={ref}
          {...contentProps}
          css={{
            zIndex: isExtensionAddToNotes ? 2147483646 : 1001,
          }}
        >
          <VisuallyHidden>
            <AlertDialogPrimitive.AlertDialogTitle />
            <AlertDialogPrimitive.AlertDialogDescription />
          </VisuallyHidden>
          {props.children}
        </StyledContent>
      </AlertDialogPrimitive.AlertDialogPortal>
    </AlertDialogPrimitive.Root>
  );
});

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
});

const StyledOverlay = styled(AlertDialogPrimitive.Overlay, {
  backgroundColor: theme.colors.overlayBlackAlpha9,
  position: 'fixed',
  inset: 0,
  zIndex: 1001,
  pointerEvents: 'auto',
  // Ensure overlay is contained within shadow DOM
  contain: 'layout style paint',
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
});

const CloseButton = styled(BasicButton, {
  position: 'absolute',
  top: 20,
  right: 20,
  border: 'none',
  cursor: 'pointer',
});
const StyledContent = styled(AlertDialogPrimitive.AlertDialogContent, {
  backgroundColor: 'white',
  borderRadius: 16,
  overflow: 'auto',
  zIndex: 1001,
  transform: 'translate(-50%, -50%)',
  top: '50%',
  left: '50%',
  maxHeight: '90vh',
  maxWidth: '90vw',
  boxShadow:
    'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  position: 'fixed',
  pointerEvents: 'auto',
  // Prevent auto-scrolling issues in shadow DOM
  scrollMargin: 0,
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  '&:focus': {
    outline: 'none',
    // Prevent focus scroll behavior
    scrollMargin: 0,
  },
  '&:focus-visible': {
    outline: 'none',
    scrollMargin: 0,
  },
  variants: {
    isMobileStickyBottom: {
      true: {
        '@sm': {
          transform: 'none',
          maxWidth: '100%',
          width: '100%',
          top: 'auto',
          bottom: 0,
          left: 0,
          borderRadius: '16px 16px 0 0',
          animation: 'none',
        },
      },
      false: {},
    },
  },
});

export const DialogClose = React.forwardRef<
  HTMLButtonElement,
  { onClick: () => void }
>((props, ref) => (
  <CloseButton
    ref={ref}
    onClick={props.onClick}
    trackingName="dialogClose"
    aria-label="Close"
    css={{
      zIndex: 999,
    }}
  >
    <CrossIcon width={'14'} />
  </CloseButton>
));

const ButtonWrapper = styled('div', {
  position: 'absolute',
  top: 8,
  right: 16,
  border: 'none',
  cursor: 'pointer',
  color: theme.colors.gray8,
  opacity: 0.5,
  zIndex: 999,
});

export const DialogCloseWIthCrossIcon = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void;
    ariaLabel?: string;
    dataId?: string;
    dataTestId?: string;
    trackingName?: string;
    trackingProperties?: { sectionName?: string };
  }
>((props, ref) => (
  <ButtonWrapper>
    <CloseIcon
      ref={ref}
      onClick={props.onClick}
      role="button"
      tabIndex={0}
      aria-label={props.ariaLabel || 'Close dialog'}
      data-id={props.dataId || 'dialog-close-button'}
      data-testid={props.dataTestId || 'dialog-close-button'}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.onClick();
        }
      }}
      {...{
        trackingName: props.trackingName || 'dialogClose',
        trackingProperties: {
          sectionName: props.trackingProperties?.sectionName || 'dialog',
        },
      }}
    >
      <Icon name={'circleXmark'} variant={'regular'} width={20} height={20} />
    </CloseIcon>
  </ButtonWrapper>
));
